import React from 'react';
import { Box, Button, Stack, Typography, alpha } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { getAccentColor, getBodyColor } from './shared';

const normalizePath = (path) => { const value = String(path || '').trim(); return value ? (value.startsWith('/') ? value : `/${value}`) : ''; };

const SidebarContentSection = ({ section, sectionKey, sectionIndex, sidebarItems = [], sidebarButtons = [], renderHtmlTextBlock, renderPositionableElement }) => {
  const location = useLocation();
  const accent = getAccentColor(section) || '#4F46E5';
  const bodyColor = getBodyColor(section) || '#64748B';
  const items = sidebarItems.map((item) => ({ label: String(item?.label || '').trim(), path: normalizePath(item?.path) })).filter((item) => item.label && item.path);
  const buttons = sidebarButtons.map((item) => ({ label: String(item?.label || '').trim(), path: normalizePath(item?.path) })).filter((item) => item.label && item.path);

  return (
    <Box component="section" sx={{ py: { xs: 1, md: 1.6 }, width: '100%' }}>
      <Box sx={{ display: 'grid', gap: { xs: 1.5, md: 2.4 }, gridTemplateColumns: { xs: '1fr', md: '300px 1fr' }, alignItems: 'start' }}>
        
        {/* Sticky Sidebar Navigation */}
        {renderPositionableElement({
          section, index: sectionIndex, elementKey: `${sectionKey}-sidebar-nav`,
          children: (
            <Box sx={{ p: 3, borderRadius: '32px', backgroundColor: '#F8FAFC', border: `1px solid ${alpha('#0F172A', 0.05)}`, position: { md: 'sticky' }, top: { md: 100 } }}>
              <Stack spacing={2}>
                {(section.sidebarTitle || section.title) && (
                  <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', color: '#0F172A', px: 1 }}>{section.sidebarTitle || section.title}</Typography>
                )}
                <Stack spacing={0.5}>
                  {items.length ? items.map((item, index) => {
                    const active = location.pathname === item.path;
                    return (
                      <Button key={`${sectionKey}-nav-${index}`} component={RouterLink} to={item.path} disableRipple fullWidth sx={{ justifyContent: 'flex-start', px: 2, py: 1.2, borderRadius: '6px', textTransform: 'none', color: active ? accent : '#475569', fontWeight: active ? 800 : 600, backgroundColor: active ? alpha(accent, 0.1) : 'transparent', '&:hover': { backgroundColor: active ? alpha(accent, 0.15) : alpha('#0F172A', 0.04) }, transition: 'all 0.2s' }}>
                        {item.label}
                      </Button>
                    );
                  }) : <Typography sx={{ color: '#94A3B8', fontSize: '0.9rem', px: 1 }}>Add links in editor.</Typography>}
                </Stack>
              </Stack>
            </Box>
          ),
        })}

        {/* Main Content Area */}
        <Stack spacing={4}>
          {renderPositionableElement({
            section, index: sectionIndex, elementKey: `${sectionKey}-content-panel`,
            children: (
              <Box sx={{ p: { xs: 3, md: 5 }, borderRadius: '32px', backgroundColor: '#FFFFFF', border: `1px solid ${alpha('#0F172A', 0.05)}`, boxShadow: `0 4px 24px ${alpha('#0F172A', 0.03)}` }}>
                {section.body && renderHtmlTextBlock(section.body, { color: bodyColor, lineHeight: 1.8, fontSize: '1.1rem' })}
              </Box>
            ),
          })}
          
          {buttons.length > 0 && (
            <Box sx={{ p: { xs: 3, md: 5 }, borderRadius: '32px', backgroundColor: alpha(accent, 0.03), border: `1px solid ${alpha(accent, 0.1)}` }}>
              <Stack spacing={3}>
                <Box>
                  <Typography sx={{ color: '#0F172A', fontWeight: 800, fontSize: '1.5rem', mb: 1 }}>{section.sidebarCtaTitle || 'Next Steps'}</Typography>
                  {section.sidebarCtaSubtitle && <Typography sx={{ color: '#475569', fontSize: '1rem' }}>{section.sidebarCtaSubtitle}</Typography>}
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
                  {buttons.map((item, index) => (
                    <Button key={`${sectionKey}-btn-${index}`} component={RouterLink} to={item.path} variant="contained" sx={{ borderRadius: '100px', px: 4, py: 1.5, textTransform: 'none', fontWeight: 800, backgroundColor: index === 0 ? accent : '#0F172A', color: '#FFFFFF', boxShadow: index === 0 ? `0 8px 20px ${alpha(accent, 0.3)}` : 'none', '&:hover': { backgroundColor: index === 0 ? accent : '#1E293B', transform: 'translateY(-2px)' } }}>
                      {item.label}
                    </Button>
                  ))}
                </Stack>
              </Stack>
            </Box>
          )}
        </Stack>
      </Box>
    </Box>
  );
};
export default SidebarContentSection;
